import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticleConfiguration } from "../../shared/utils/getArticleInformation"
import { getCustomer } from "../../shared/utils/getCustomer"
import { getSkuCalculatedPrice } from "../../shared/utils/getPrices"
import { getStoreKeysWithChannels } from "../../shared/utils/getStoreKeysWithChannels"
import {
  addCustomLineItemAction,
  findCustomLineItemUpdates,
  getUpdatedLineItem,
  removeCustomLineItemAction,
} from "../utils/cartHelpers"
import { getCart } from "../utils/getCart"

export const mutationUpdateCustomLineItemsConfiguration = {
  async updateCustomLineItemsConfiguration(parent, args, context, info) {
    const { cartId, cartKey, items, cdn, lang } = args
    let actions = []

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    const customerCart = await getCart(cartId, cartKey)

    const { customerId, customLineItems, store, totalPrice } = customerCart

    const currencyCode = totalPrice.currencyCode
    const customer = await getCustomer({ id: customerId })

    if (!customer) {
      throw new Error("Customer not found")
    }

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    if (!customerCart || customerCart.cartState !== "Active") {
      throw new Error("Cart was not found or is not in active state")
    }

    if (customerCart?.customLineItems.length <= 0 || !items || items.length <= 0) {
      return customerCart
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const commerceApi = CommerceService.api()

    const storeKeysWithChannels = await getStoreKeysWithChannels(customer?.stores)

    await Promise.all(
      items.map(async (item) => {
        // if slug was not found, return null as nothing to do
        if (!item?.slug || item?.slug?.length <= 0) {
          return null
        }

        const targetCustomLineItem = customLineItems?.find((customLineItem) => item?.slug === customLineItem?.slug)

        // no item was found
        if (!targetCustomLineItem) return null

        const targetCustomLineItemCustomFields = targetCustomLineItem?.custom?.fields

        const lineItemUpdates = findCustomLineItemUpdates(targetCustomLineItemCustomFields, item)

        if (!lineItemUpdates) return null

        const {
          additionalInformationUpdated,
          sendProductTextUpdated,
          sendTechnicalManualUpdated,
          sendCertificateWithOfferUpdated,
          discountUpdated,
          priceUpdated,
          additionalCommentUpdated,
          positionUpdated,
        } = lineItemUpdates

        // if none of the items have changed, nothing to do
        if (
          !additionalInformationUpdated &&
          !sendProductTextUpdated &&
          !sendTechnicalManualUpdated &&
          !sendCertificateWithOfferUpdated &&
          !discountUpdated &&
          !priceUpdated &&
          !additionalCommentUpdated &&
          !positionUpdated
        ) {
          return null
        }

        const articleConfig = await getArticleConfiguration({
          sku: targetCustomLineItemCustomFields.sku,
          productKey: targetCustomLineItemCustomFields.productId,
        })
        if (!articleConfig) {
          actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))
        }

        const lineItemCustomPrice =
          typeof targetCustomLineItemCustomFields?.customPrice?.centAmount === "number"
            ? targetCustomLineItemCustomFields?.skuPrice * 100 ||
              targetCustomLineItemCustomFields.customPrice.centAmount
            : null

        // recalculate the price, if nothing has changed, it will return the same money object as it was already stored
        const price = await getSkuCalculatedPrice({
          sku: targetCustomLineItemCustomFields?.sku,
          channels: storeKeysWithChannels,
          group: customer?.customerGroup,
          country: customerCart?.billingAddress?.country,
          discountPercent: discountUpdated ? item?.discountPercent : targetCustomLineItemCustomFields?.discountPercent,
          customPrice: priceUpdated ? Number(item?.newPrice) * 100 : lineItemCustomPrice,
          currencyCode,
          articleConfig,
          quantity: targetCustomLineItemCustomFields.quantity,
        })

        // no price was found
        if (!price) return null

        // if changes were detected, remove the line item and add the updated version of it
        actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))

        // if configuration changes, but price does not, use old price (commercetools looses couple of digits when updating highPrecision prices)
        const priceToUse =
          priceUpdated || discountUpdated
            ? price
            : { key: price?.key, skuPrice: price.skuPrice, value: targetCustomLineItem.money }

        const updatedCustomLineItem = getUpdatedLineItem({
          targetCustomLineItem,
          valuesToUpdate: item,
          customTypeId: targetCustomLineItem?.custom?.type?.id,
          price: priceToUse,
          lineItemUpdates,
        })

        actions.push(
          addCustomLineItemAction(
            updatedCustomLineItem,
            currencyCode,
            priceToUse,
            cartId,
            targetCustomLineItem.quantity,
          ),
        )
      }),
    )

    // remove all null items
    actions = actions.filter((n) => n)

    // no need for any action, since nothing was matched
    if (actions?.length <= 0) {
      return customerCart
    }

    const payload = {
      version: customerCart.version,
      actions,
      ...(store && { storeKey: store.key }),
    }

    let updatedCart = (
      await commerceApi
        .carts()
        .withId({ ID: customerCart.id })
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    )?.body

    return updatedCart
  },
}
